import AddIcon from '@mui/icons-material/Add';
import React, { FC, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { useSWRConfig } from 'swr';
import createUserColorSwatch from '../../../api/user/createUserColorSwatch';
import deleteUserColorSwatch from '../../../api/user/deleteUserColorSwatch';
import { TenantContext } from '../../../contexts/tenantContext';
import { colors } from '../../../theme';
import useFetcher from '../../../utils/hooks/useFetcher';
import ColorSwatch, { ColorSwatchInterface } from '../../ColorSwatch/colorSwatch';
import ColorSwatchModal from '../../Modals/ColorSwatchModal/ColorSwatchModal';
import { ColorSwatchCreationInterface } from '../../Modals/ColorSwatchModal/colorSwatchModalInterface';
import { AddColorSwatchButtonStyled, ColorSwatchContainer, ColorSwatchHeader, ColorSwatchSubTitle, ColorSwatchTitleStyled } from './styled';

const ColorSwatches: FC = () => {
    const fetcher = useFetcher();
    const { mutate } = useSWRConfig();
    const { t } = useTranslation('settings');
    const [selectedSchema] = useContext(TenantContext) ?? [];
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const urlUserColorSwatches = `${process.env.REACT_APP_API_URL}/user/color-swatches/`;
    const { data: userColorSwatches } = useSWR(urlUserColorSwatches, fetcher);
    const newColorSwatch: ColorSwatchCreationInterface = {
        id: undefined,
        label: '',
        rank: userColorSwatches ? userColorSwatches.length + 1 : 1,
        colorSwatch: Array(4).fill(colors.blue000),
    };
    const [selectedColorSwatch, setSelectedColorSwatch] = useState<ColorSwatchCreationInterface>(newColorSwatch);
    const handleSelectColorSwatch = (colorSwatch: ColorSwatchInterface) => {
        const colorSwatchToApply: ColorSwatchCreationInterface = {
            id: colorSwatch.id,
            label: colorSwatch.label,
            rank: colorSwatch.rank,
            colorSwatch: colorSwatch.colors,
        };
        setSelectedColorSwatch(colorSwatchToApply);
        setOpenModal(true);
        setIsEdit(true);
    };
    const handleAddColorSwatch = () => {
        setSelectedColorSwatch(newColorSwatch);
        setOpenModal(true);
        setIsEdit(false);
    };
    const mutateUserColorSwatches = () => {
        mutate(`${process.env.REACT_APP_API_URL}/user/color-swatches/`);
    };
    const handleUpdateColorSwatch = async (colorSwatchToCreate: ColorSwatchCreationInterface) => {
        const successMessage = isEdit ? t('colorSwatches.successUpdateMessage') : t('colorSwatches.successCreationMessage');
        const errorMessage = isEdit ? t('colorSwatches.errorUpdateMessage') : t('colorSwatches.errorCreationMessage');
        await createUserColorSwatch(colorSwatchToCreate.id, colorSwatchToCreate.label, colorSwatchToCreate.rank, colorSwatchToCreate.colorSwatch, successMessage, errorMessage, mutateUserColorSwatches, selectedSchema || '');
        setSelectedColorSwatch(newColorSwatch);
        setOpenModal(false);
    };
    const handleDeleteColorSwatch = async (colorSwatchIdToDelete: number) => {
        await deleteUserColorSwatch(colorSwatchIdToDelete, t('colorSwatches.successDeleteMessage'), t('colorSwatches.errorDeleteMessage'), mutateUserColorSwatches, selectedSchema || '');
        setSelectedColorSwatch(newColorSwatch);
        setOpenModal(false);
    };

    return (
        <>
            <ColorSwatchHeader>
                <ColorSwatchTitleStyled>
                    {t('colorSwatches.title')}
                </ColorSwatchTitleStyled>
                <ColorSwatchSubTitle>
                    {t('colorSwatches.subTitle')}
                </ColorSwatchSubTitle>
                {userColorSwatches && userColorSwatches.length === 0 && (
                    <ColorSwatchSubTitle>
                        {t('colorSwatches.emptyTitle')}
                    </ColorSwatchSubTitle>
                )}
            </ColorSwatchHeader>
            <ColorSwatchContainer>
                {userColorSwatches && userColorSwatches.map((colorSwatch: ColorSwatchInterface) => (
                    <ColorSwatch key={colorSwatch.id} colorSwatch={colorSwatch} handleSelectColorSwatch={handleSelectColorSwatch} />
                ))}
                <AddColorSwatchButtonStyled
                    onClick={handleAddColorSwatch}
                    startIcon={<AddIcon sx={{ width: '24px' }} />}
                    disableRipple
                />
            </ColorSwatchContainer>
            <ColorSwatchModal handleDeleteColorSwatch={handleDeleteColorSwatch} handleUpdateColorSwatch={handleUpdateColorSwatch} isEdit={isEdit} openModal={openModal} selectedColorSwatch={selectedColorSwatch} setOpenModal={setOpenModal} />
        </>
    );
};

export default ColorSwatches;
